import { reportOnce } from '@protector/api-client';
import { RegionType } from '@protector/entities/region/constants';
import type { Action, Dictionary } from '@protector/utils/types';
import type { AxiosObservable } from 'axios-observable/dist/axios-observable.interface';
import { getBorerRiskDataPage, getConfigurationParameters } from 'pages/borer-risk/borer-risk-map/borer-risk-map.service';
import { ofType, type ActionsObservable } from 'redux-observable';
import { of } from 'rxjs/internal/observable/of';
import { catchError, concatMap, map } from 'rxjs/operators';
import type { ConfigurationParameterDTO, LimitValues, RiskDTO, pageRiskDTO } from '../../pages/borer-risk/borer-risk-map/borer-risk.models';
import {
  BorerRiskActionTypes,
  LoadBorerRiskParametersFailure,
  LoadBorerRiskParametersSuccess,
  LoadBorerRisksFailure,
  LoadBorerRisksSuccess,
  ReloadBorerRisks
} from './borer-risk.actions';
import { getParameterLimits, getRiskDTOFromResponse, getRisksPerRegion } from './borer-risk.mappers';
import type { BorerRiskParametersPayload, BorerRiskPayload } from '@protector/entities/borer-risk/models';

/**
 * Loads the parameters configured for some property
 * @param action$
 */
export const handleLoadBorerRiskParameters = (action$: ActionsObservable<Action<BorerRiskParametersPayload>>) =>
  action$.pipe(
    ofType(BorerRiskActionTypes.LOAD_CONFIGURATION_PARAMETERS),
    map((action: Action<BorerRiskParametersPayload>) => action.payload ?? ({} as BorerRiskParametersPayload)),
    concatMap(payload => {
      return getConfigurationParameters(payload.propertyId, payload.seasonId).pipe(
        map(response => {
          const result: Dictionary<ConfigurationParameterDTO> = {};
          response.data.pr.forEach(par => {
            result[par.id] = par;
          });
          return LoadBorerRiskParametersSuccess(result);
        }),
        catchError(() => of(LoadBorerRiskParametersFailure('Error loading parameters')))
      );
    })
  );

/**
 * Function that choose the endpoint that will be called according to payload
 * @payload The payload of the action
 */
const getRiskRequest = (payload: BorerRiskPayload): AxiosObservable<RiskDTO | pageRiskDTO> => {
  return getBorerRiskDataPage(payload.propertyId, payload.seasonId, 1000, payload.cursor, payload?.year);
};

/**
 * Loads Borer Risks for all the areas
 * Then Calculates the Borer Risk for the regions that have these areas as children
 * @param action$
 */
export const handleLoadBorerRisks = (action$: ActionsObservable<Action<BorerRiskPayload>>) =>
  action$.pipe(
    ofType(BorerRiskActionTypes.LOAD_BORER_RISKS, BorerRiskActionTypes.RELOAD_BORER_RISKS),
    map((action: Action<BorerRiskPayload>) => action.payload ?? ({} as BorerRiskPayload)),
    concatMap((payload: BorerRiskPayload) =>
      getRiskRequest(payload).pipe(
        map(response => response.data),
        map(data => {
          if (!data?.content) {
            reportOnce(
              'borer-risk.epics:handleLoadBorerRisks:missingContent',
              () => new Error('borer-risk.epics: getRiskRequest data.content missing -- falling back to {}'),
              { errorType: 'StateFallback', domain: 'BorerRisk' }
            );
          }
          // If the endpoint return an empty content for the last call return the actual risk
          if (data?.is_last && !Object.keys(data?.content ?? {}).length) {
            return LoadBorerRisksSuccess(payload.risks ?? {}, payload.limitValues ?? { areas: {}, regions: {} });
          }

          const { regions } = payload;

          const totalRiskData = {
            ...payload.risks,
            ...getRiskDTOFromResponse(data)
          };

          const risksPerRegion = getRisksPerRegion(regions, totalRiskData);

          Object.keys(risksPerRegion).forEach(key => {
            totalRiskData[key] = risksPerRegion[key];
          });

          const riskRegionKeys = Object.keys(totalRiskData).filter(key => regions[key]?.type === RegionType.REGION);
          const riskAreaKeys = Object.keys(totalRiskData).filter(key => regions[key]?.type === RegionType.AREA);

          const limitValues: LimitValues = {
            areas: getParameterLimits(totalRiskData, riskAreaKeys),
            regions: getParameterLimits(totalRiskData, riskRegionKeys)
          };

          if (!data.is_last) {
            const cursor = data?.cursor ? (data as pageRiskDTO).cursor : null;
            return ReloadBorerRisks(payload.propertyId, payload.seasonId, limitValues, regions, totalRiskData, cursor);
          }
          return LoadBorerRisksSuccess(totalRiskData, limitValues);
        }),
        catchError(() => of(LoadBorerRisksFailure('Error loading area risks')))
      )
    )
  );
